import { useStore } from "@/core/services/store";
import { setXentralConfig } from "@/composables/useSalesChannelManagementApp";
import {
  amazonDefaultRegion,
  getAuthorizeUrlByRegion
} from "@/components/ExternalApps/SalesChannelManagementApp/utility/amazonRegions";
import { useRoute } from "vue-router/composables";
import { isEmpty } from "lodash";
import { Toast } from "@/core/plugins/swal";
import i18n from "@/core/plugins/vue-i18n";
import { computed } from "vue";
import { useMigrationIntegration } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useMigrationIntegration";
import { useApp } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useApp";
import useOauth from "@/components/ExternalApps/SalesChannelManagementApp/composables/useOauth";

export const useAmazonOauth = () => {
  const store = useStore();
  const route = useRoute();
  const { base64ToUtf8, utf8ToBase64 } = useOauth();

  const xentralConfigData = computed(
    () => store.getters.xentralConfig?.data ?? []
  );
  const queryState = computed(() => {
    return base64ToUtf8(xentralConfigData.value?.["state"]);
  });

  const { selectIntegration } = useApp();
  const { show, startMigration, selectedChannel, selectedChannelId } =
    useMigrationIntegration();

  async function getAccessToken() {
    const state = store.getters.migrationOAuthState;
    const xentralConfig = store.getters.xentralConfig?.data;
    const fromUrl = xentralConfig?.baseUrl?.replace(/\/$/, "") || "";
    const endpoint = xentralConfig?.xentralAppUri || "";

    // Update state
    Object.assign(state, {
      migration: true,
      softMigrated: true,
      from_url: `${fromUrl.replace(/\/+$/, "")}/${endpoint.replace(
        /^\/+/,
        ""
      )}`,
      redirect_uri:
        process.env.VUE_APP_AMAZON_APP_APPLICATION_MIGRATION_REDIRECT_URL,
      licenseId: store.getters.licenseId,
      origin: route.params?.area,
      uuid: selectedChannel.value?.uuid,
      region: selectedChannel.value?.region,
      project_id: store.getters?.selectedIntegration?.id
    });

    // Construct the OAuth URL
    const params = new URLSearchParams({
      application_id: process.env.VUE_APP_AMAZON_APP_APPLICATION_ID,
      redirect_uri:
        process.env.VUE_APP_AMAZON_APP_APPLICATION_MIGRATION_REDIRECT_URL,
      state: utf8ToBase64(state)
    });

    resetXentralConfig();
    const baseUrl = await getBaseUrl(selectedChannel.value?.region);

    // Redirect to the OAuth URL
    window.open(`${baseUrl}?${params}`, "_parent");
  }

  async function getBaseUrl(region) {
    return getAuthorizeUrlByRegion(region) ?? amazonDefaultRegion.url;
  }

  async function startAmazonOAuthMigration() {
    if (isEmpty(queryState.value)) return;

    Toast.fire({
      icon: "success",
      title: i18n.t("salesChannelManagementApp.amazon.migrationSuccess")
    }).then(() => {
      // ignore promise
    });

    selectedChannelId.value.channelId = queryState.value.uuid;
    await startMigration(queryState.value);
    resetXentralConfig();
    await store.dispatch("setMigrationOAuthState", undefined);
  }

  async function applyAmazonOAuthValues() {
    if (isEmpty(queryState.value)) return;

    // Opens the migration modal
    if (queryState.value?.migration) {
      show.value = true;
      return;
    }

    // Opens settings area
    const selectedIntegration = store.getters.integrationProject(
      queryState.value.project_id
    );
    await selectIntegration(
      selectedIntegration,
      "settings",
      "settings",
      "wizard"
    );

    await Toast.fire({
      icon: "success",
      title: i18n.t("salesChannelManagementApp.newTokenSuccess")
    });

    resetXentralConfig();
  }

  function resetXentralConfig() {
    // Remove state property from the xentralConfig vuex store
    let xentralData = store.getters.xentralConfig;

    // Create a new object excluding the 'state' property
    let newData = { ...xentralData.data };
    delete newData.state;

    let updatedXentralConfig = { ...xentralData, data: newData };

    setXentralConfig(updatedXentralConfig);
  }

  return {
    startAmazonOAuthMigration,
    applyAmazonOAuthValues,
    getAccessToken
  };
};
